import { type JSX, useMemo, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button, ButtonGroup, Select, SelectItem, SelectSection } from '@heroui/react';
import clsx from 'clsx';
import { Clock, Search, SortAsc } from 'lucide-react';

import { usePRDBreakdownBatch } from '~/hooks/usePRDBreakdown';
import { usePRDSessionStatusBatch } from '~/hooks/usePRDSessionStatus';
import { useProducts } from '~/hooks/useProducts';
import {
  ALL_PRODUCTS_KEY,
  ALL_REPOS_KEY,
  CHIP_TO_STATUSES,
  UNASSIGNED_PRODUCT_KEY,
  type UseProjectFilteringReturn,
} from '~/hooks/useProjectFiltering';

import { Feedback } from '~/components/Feedback';
import { ProjectStatusChips } from '~/components/ProjectStatusChips';
import { ScrollArea } from '~/components/ScrollArea';
import { Tooltip } from '~/components/Tooltip';

import type { ProjectProps } from '~/types';

import { ProjectCard } from './ProjectCard';

// Section configuration for status-grouped collapsible sections
type ProjectSection = 'TODO' | 'IN_PROGRESS' | 'DONE';

const SECTION_CONFIG: Array<{ dotColor: string; key: ProjectSection; label: string }> = [
  { key: 'TODO', label: 'Todo', dotColor: 'bg-primary-400' },
  { key: 'IN_PROGRESS', label: 'In Progress', dotColor: 'bg-secondary-400' },
  { key: 'DONE', label: 'Done', dotColor: 'bg-success-400' },
];

export interface ProjectsListProps {
  filtering: UseProjectFilteringReturn;
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string | null) => void;
  onProjectStatusChange: (projectId: string, status: ProjectProps['status']) => void;
  onProjectUpdate: (projectId: string, updates: Partial<ProjectProps>) => void;
  onProjectDelete: (projectId: string) => void;
  onGenerateTasksFromPRD: (projectId: string, prdContent: string) => Promise<void>;
  onToggleSidebar: () => void;
  isLoading?: boolean;
  error?: string | null;
  isStale?: boolean;
  isOnline?: boolean;
}

export function ProjectsList(props: ProjectsListProps): JSX.Element {
  const {
    error,
    filtering,
    isLoading,
    isOnline = true,
    isStale = false,
    onGenerateTasksFromPRD,
    onProjectDelete,
    onProjectSelect,
    onProjectStatusChange,
    onProjectUpdate,
    onToggleSidebar,
    selectedProjectId,
  } = props;

  const {
    availableRepos,
    clearChips,
    clearProductFilter,
    clearRepositoryFilter,
    filteredProjects,
    productFilter,
    repositoryFilter,
    searchQuery,
    selectedChips,
    setProductFilter,
    setRepositoryFilter,
    setSearchQuery,
    setSortOption,
    sortOption,
    toggleChip,
  } = filtering;

  // Collapsible section state — persisted to localStorage
  const [collapsedSections, setCollapsedSections] = useState<Set<ProjectSection>>(() => {
    try {
      const stored = localStorage.getItem('projects-collapsed-sections');
      if (stored) {
        const parsed: unknown = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set(
            parsed.filter(
              (v): v is ProjectSection => v === 'TODO' || v === 'IN_PROGRESS' || v === 'DONE',
            ),
          );
        }
      }
    } catch {
      /* ignore */
    }
    return new Set();
  });
  const toggleSection = (section: ProjectSection): void => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      try {
        localStorage.setItem('projects-collapsed-sections', JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Group projects by status section
  const groupedProjects = useMemo(() => {
    const groups: Record<ProjectSection, ProjectProps[]> = {
      TODO: [],
      IN_PROGRESS: [],
      DONE: [],
    };
    const todoStatuses = new Set(CHIP_TO_STATUSES.TODO);
    const inProgressStatuses = new Set(CHIP_TO_STATUSES.IN_PROGRESS);

    for (const project of filteredProjects) {
      if (todoStatuses.has(project.status)) {
        groups.TODO.push(project);
      } else if (inProgressStatuses.has(project.status)) {
        groups.IN_PROGRESS.push(project);
      } else {
        groups.DONE.push(project);
      }
    }
    return groups;
  }, [filteredProjects]);

  const { data: products = [] } = useProducts();

  // Fetch all project breakdown statuses in a single batch query
  // Individual ProjectCards read from this shared cache (zero additional API calls)
  usePRDBreakdownBatch();

  // Fetch all project PRD session statuses in a single batch query
  // Individual ProjectCards read from this shared cache (zero additional API calls)
  usePRDSessionStatusBatch();

  const hasActiveFilters =
    productFilter.size > 0 ||
    repositoryFilter.size > 0 ||
    selectedChips.length > 0 ||
    !!searchQuery.trim();

  if (isLoading) {
    return (
      <div className="w-full p-6 h-full">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full p-6 ">
        <Feedback type="error" description={error} title={null} />
      </div>
    );
  }

  return (
    <div className="w-full bg-background text-foreground flex flex-col h-full overflow-hidden">
      {/* Row 1: Title + optional reset-filters + Collapse */}
      <div className="flex items-center gap-2 px-4 pt-5 pb-2 shrink-0">
        {(isStale || !isOnline) && (
          <Tooltip
            content={
              !isOnline
                ? 'Offline — showing cached data'
                : 'Unable to refresh — showing cached data'
            }
          >
            <ExclamationTriangleIcon className="h-4 w-4 text-warning shrink-0 mr-2" />
          </Tooltip>
        )}
        <h3 className="text-xl font-semibold text-foreground">Projects</h3>
        <div className="flex-1" />
        {hasActiveFilters && (
          <Button
            aria-label="Reset all filters"
            size="sm"
            variant="light"
            className="text-muted hover:text-foreground"
            onPress={() => {
              clearProductFilter();
              clearRepositoryFilter();
              clearChips();
              setSearchQuery('');
            }}
          >
            Reset filters
          </Button>
        )}
        <Button
          aria-label="Collapse sidebar"
          isIconOnly
          onPress={onToggleSidebar}
          size="sm"
          variant="light"
        >
          <ChevronLeftIcon className="h-4 w-4 text-muted" />
        </Button>
      </div>

      {/* Row 2: Product Filter */}
      {products.length > 0 && (
        <div className="px-4 pb-2 pt-1 shrink-0">
          <Select
            aria-label="Filter by product"
            classNames={{
              trigger: 'bg-default',
            }}
            data-testid="product-filter"
            placeholder="All products"
            selectedKeys={productFilter}
            onSelectionChange={keys => {
              const selected = keys === 'all' ? new Set<string>() : new Set(keys as Set<string>);
              // Always reset repo when product context changes — they are hierarchical,
              // not independent. A repo selection from Product X is meaningless in
              // the Unassigned or Product Y context.
              clearRepositoryFilter();
              if (selected.has(ALL_PRODUCTS_KEY)) {
                clearProductFilter();
              } else {
                setProductFilter(selected);
              }
            }}
            size="sm"
          >
            <SelectSection classNames={{ heading: 'font-bold text-foreground' }} title="Product">
              {[
                <SelectItem key={ALL_PRODUCTS_KEY} textValue="All products">
                  All products
                </SelectItem>,
                <SelectItem key={UNASSIGNED_PRODUCT_KEY} textValue="Unassigned">
                  Unassigned
                </SelectItem>,
                ...products.map(product => (
                  <SelectItem key={product.id} textValue={product.name}>
                    {product.name}
                  </SelectItem>
                )),
              ]}
            </SelectSection>
          </Select>
        </div>
      )}

      {/* Row 3: Repository Filter — always shown so "All repos" remains accessible
          even when the current product context has no specific repos to drill into. */}
      <div className="px-4 pb-2 shrink-0">
        <Select
          aria-label="Filter by repository"
          classNames={{
            trigger: 'bg-default',
          }}
          data-testid="repository-filter"
          placeholder="All repos"
          selectedKeys={repositoryFilter}
          selectionMode="multiple"
          onSelectionChange={keys => {
            const selected = keys === 'all' ? new Set<string>() : new Set(keys as Set<string>);
            if (selected.has(ALL_REPOS_KEY)) {
              clearRepositoryFilter();
            } else {
              setRepositoryFilter(selected);
            }
          }}
          size="sm"
        >
          <SelectSection
            classNames={{ heading: 'font-bold text-foreground' }}
            title="Git Repository"
          >
            {[
              <SelectItem key={ALL_REPOS_KEY} textValue="All repos">
                All repos
              </SelectItem>,
              ...availableRepos.map(repo => (
                <SelectItem key={repo.id} textValue={repo.fullName}>
                  {repo.fullName}
                </SelectItem>
              )),
            ]}
          </SelectSection>
        </Select>
      </div>

      {/* Row 3b: Status chip filter */}
      <ProjectStatusChips
        selectedChips={selectedChips}
        toggleChip={toggleChip}
        clearChips={clearChips}
      />

      {/* Row 4: Search + Sort */}
      <div className="flex items-center gap-2 px-4 pb-4 shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted pointer-events-none" />
          <input
            aria-label="Search projects"
            className="w-full h-8 pl-7 pr-2 text-sm rounded-lg bg-default text-foreground placeholder:text-muted outline-none focus:ring-1 focus:ring-primary"
            data-testid="project-search-input"
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search..."
            type="text"
            value={searchQuery}
          />
        </div>
        <span className="text-sm text-muted">Sort:</span>
        <ButtonGroup size="sm">
          <Tooltip content="Newest first">
            <Button
              aria-label="Sort by newest first"
              color={sortOption === 'newest' ? 'primary' : 'default'}
              isIconOnly
              onPress={() => setSortOption('newest')}
            >
              <Clock className="h-4 w-4" />
            </Button>
          </Tooltip>
          <Tooltip content="Oldest first">
            <Button
              aria-label="Sort by oldest first"
              color={sortOption === 'oldest' ? 'primary' : 'default'}
              isIconOnly
              onPress={() => setSortOption('oldest')}
            >
              <SortAsc className="h-4 w-4" />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </div>

      {/* Project list — grouped by status */}
      <ScrollArea className="flex-1 overflow-y-auto pr-4 pt-2" padding={false}>
        {filteredProjects.length === 0 ? (
          <Feedback
            hideIcon
            description={
              productFilter.size > 0 ||
              repositoryFilter.size > 0 ||
              selectedChips.length > 0 ||
              searchQuery.trim()
                ? 'No projects match the current filters'
                : 'No projects yet — your agent will create one automatically when you start a new task with Compounds within your agentic IDE'
            }
            type={null}
          />
        ) : (
          <div aria-label="Projects" role="listbox">
            {SECTION_CONFIG.map(({ dotColor, key, label }) => {
              const sectionProjects = groupedProjects[key];
              const isCollapsed = collapsedSections.has(key);
              const isEmpty = sectionProjects.length === 0;

              return (
                <div key={key} className={clsx('mb-4', isEmpty && 'opacity-40')}>
                  {/* Section header */}
                  <button
                    type="button"
                    onClick={() => toggleSection(key)}
                    className="flex items-center justify-between w-full py-2 text-sm font-semibold text-foreground-600 hover:text-foreground transition-colors"
                    aria-expanded={!isCollapsed}
                    aria-controls={`project-section-${key}`}
                  >
                    <div className="flex items-center gap-2">
                      <ChevronRightIcon
                        className={clsx(
                          'h-3.5 w-3.5 shrink-0 transition-transform duration-150 ease-out',
                          { 'rotate-90': !isCollapsed },
                        )}
                      />
                      <span className={clsx('w-2 h-2 rounded-full shrink-0', dotColor)} />
                      <span>
                        {label} ({sectionProjects.length})
                      </span>
                    </div>
                  </button>

                  {/* Collapsible project list */}
                  <div
                    id={`project-section-${key}`}
                    className={clsx('overflow-hidden transition-all duration-150 ease-out', {
                      'max-h-0': isCollapsed,
                      'max-h-[5000px]': !isCollapsed,
                    })}
                  >
                    <div className="flex flex-col gap-1.5 pt-1">
                      {sectionProjects.map(project => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                          onSelect={onProjectSelect}
                          onStatusChange={onProjectStatusChange}
                          onUpdate={onProjectUpdate}
                          onDelete={onProjectDelete}
                          onGenerateTasksFromPRD={onGenerateTasksFromPRD}
                          isSelected={project.id === selectedProjectId}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
